import { useEffect, useState } from "react";
import { Button, Form, Input, Space, TableColumnsType, message } from "antd";
import Table, { TableProps } from "antd/es/table";
import PermissionApi from "../../../../api/PermissionApi";
import { CreateTenantRoleWizardWrapper, FormWrapper, OperationWrapper, RolePermissionsWrapper, Title, TitleWrapper } from "./CreateRoleWizard.styled";

interface CreateRoleWizardProps {
    editingRoleId?: string,

    onClose: ()=> void,
    onComplete: ()=> void
}

const CreateRoleWizard: React.FC<CreateRoleWizardProps> = (props)=> {
    // api
    let permissionApi = new PermissionApi();
    const [messageApi, contextHolder] = message.useMessage();

    // form
    const [form] = Form.useForm();

    // state
    let [role, setRole] = useState<any>({name: '', remarks: ''});
    let [rolePermissions, setRolePermissions] = useState([]);
    let [submitEnabled, setSubmitEnabled] = useState(false);
    let [rolePermissionCodePaths, setRolePermissionCodePaths] = useState([]);
    let [permissionMetas, setPermissionMetas] = useState<any[]>([]);
    let [loading, setLoading] = useState<boolean>(false);
    

    // functions
    let loadRoleAndPermissions = async ()=> {
        // tenant role
        let role = (await permissionApi.getRole(props.editingRoleId)).data.data;
        form.setFieldsValue(role);
        setRole(role);
        setSubmitEnabled(true);

        // tenant role permissions
        let rolePermissions = (await permissionApi.getRolePermissions(props.editingRoleId)).data.data;
        let tenantRolePermissionCodePaths = rolePermissions.map((permission)=> permission.permissionCodePath);
        setRolePermissions(rolePermissions);
        setRolePermissionCodePaths(tenantRolePermissionCodePaths);
    };

    let loadPermissionMetas = async ()=> {
        // tenant user permission metas
        let permissionMetas = (await permissionApi.getPermissionMetas()).data.data;
        setPermissionMetas(permissionMetas);
    }

    // event
    let onNameChange = (e)=> {
        role.name = e.target.value;
        validateForm();
    };

    let onRemarksChange = (e)=> {
        role.remarks = e.target.value;
        validateForm();
    };

    let onSubmit = async ()=> {
        // save
        if (props.editingRoleId) {
            await permissionApi.updateRole(role);
        } else {
            role = (await permissionApi.createRole(role)).data.data;
            setRole(role);
        }

        // set role permissions
        await (permissionApi.setRolePermissions(role.id, rolePermissionCodePaths));
        messageApi.success('保存成功');

        // callback
        props.onComplete();
        props.onClose();
    };

    let onCancel = ()=> {
        props.onClose();
    };

    // validate
    let validateForm = ()=> {
        form.validateFields().then((values)=> {
            setSubmitEnabled(true);
        }).catch((err)=> {
            console.log(err);
        });
    }

    // effect
    useEffect(()=> {
        loadPermissionMetas();
        if (props.editingRoleId) {
            loadRoleAndPermissions();
        }
    }, [props.editingRoleId])

    // render
    
    const rowSelection: TableProps<PermissionMeta>['rowSelection'] = {
        type: 'checkbox',
        selectedRowKeys: rolePermissionCodePaths,
        onSelect: (record, selected, selectedRows) => {
            let visitPermissionMeta = (permissionMeta: PermissionMeta, onVisit: (permissionMeta: PermissionMeta)=> void)=> {
                onVisit(permissionMeta);
                if (permissionMeta.children) {
                    for (let child of permissionMeta.children) {
                        visitPermissionMeta(child, onVisit);
                    }
                }
                return;
            };

            let visitPermissionMetas = (onVisit: (TenantUserPermissionMeta: PermissionMeta)=> void)=> {
                for(let tenantUserPermissionMeta of permissionMetas) {
                    visitPermissionMeta(tenantUserPermissionMeta, onVisit);
                }
            }

            let newRolePermissionCodePaths = [].concat(rolePermissionCodePaths);
            if (selected) {
                visitPermissionMetas((tenantUserPermissionMeta)=> {
                    if (tenantUserPermissionMeta.codePath.indexOf(record.codePath) == 0 && newRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath) < 0) {
                        newRolePermissionCodePaths.push(tenantUserPermissionMeta.codePath);
                    }

                    if (record.codePath.indexOf(tenantUserPermissionMeta.codePath) == 0 && newRolePermissionCodePaths.indexOf(tenantUserPermissionMeta.codePath) < 0) {
                        newRolePermissionCodePaths.push(tenantUserPermissionMeta.codePath);
                    }
                });
            } else {
                visitPermissionMetas((permissionMeta)=> {
                    if (permissionMeta.codePath.indexOf(record.codePath) == 0 && newRolePermissionCodePaths.indexOf(permissionMeta.codePath) >= 0) {
                        let index = newRolePermissionCodePaths.indexOf(permissionMeta.codePath);
                        newRolePermissionCodePaths.splice(index, 1);
                    }
                });
            }
            setRolePermissionCodePaths(newRolePermissionCodePaths);
        },
        getCheckboxProps: (record: PermissionMeta) => ({
            name: record.codePath,
        }),
    };

    const columns: TableColumnsType<any> = [
        {
            title: '名称',
            dataIndex: 'name',
            key: 'name',
            width: '300px',
            align: 'left',
            render: (text, record, index)=> {
                return <b>{record.name}</b>
            }
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            align: 'left',
            render: (text, record, index)=> {
                return <span>{record.description}</span>
            }
        },
    ];

    return (
        <CreateTenantRoleWizardWrapper>
            <TitleWrapper>
                <Title>{props.editingRoleId ? '编辑角色' : '新增角色'}</Title>
            </TitleWrapper>

            <FormWrapper>
                <Form form={form} name="validateOnly" layout="vertical">
                    <Form.Item name="name" label="角色名称" rules={[{required: true}]} initialValue={role.name} style={{"flex": "0 0 auto"}}>
                        <Input value={role.name} onChange={onNameChange} placeholder="请输入用户名" />
                    </Form.Item>

                    <Form.Item name="remarks" label="备注" rules={[{required: false}]} initialValue={role.remarks} style={{"flex": "0 0 auto"}}>
                        <Input.TextArea style={{height: '100px'}} onChange={onRemarksChange} placeholder="请输入用户备注"/>
                    </Form.Item>

                    <Form.Item name="permissions" label="角色权限" rules={[{required: false}]} style={{"flex": "0 0 30px", "marginBottom": "0px", "overflow": "hidden"}}>
                    </Form.Item>

                    <RolePermissionsWrapper>
                        <Table
                            rowKey={(record)=> record.codePath}
                            columns={columns} 
                            rowSelection={rowSelection}
                            dataSource={permissionMetas}
                            loading={loading}
                            pagination={false}
                        />
                    </RolePermissionsWrapper>
                </Form>
            </FormWrapper>

            <OperationWrapper>
                <Space size="middle">
                    <Button type="primary" disabled={!submitEnabled} onClick={onSubmit}>确定</Button>
                    <Button type="primary" danger onClick={onCancel}>取消</Button>
                </Space>
            </OperationWrapper>
        </CreateTenantRoleWizardWrapper>
    )
}

interface PermissionMeta {
    id: number;
    name: string;
    description: string;
    code: string;
    codePath: string;
    children: PermissionMeta[];
}

export default CreateRoleWizard;

